/**
 * Google Calendar Integration for Shift Management
 * 
 * This service provides integration between Google Calendar and the shift management system.
 * Features:
 * - Sync shifts to Google Calendar
 * - Import calendar events as shifts
 * - Handle holiday conflicts with shifts
 * - Two-way synchronization
 */

import { supabase } from '@/integrations/supabase/client';
import { Shift, DayShift } from '@/types';
import { Holiday } from '@/lib/scheduling';
import { format, parseISO, addHours } from 'date-fns';

interface GoogleCalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone?: string;
  };
  end: {
    dateTime: string;
    timeZone?: string;
  };
  location?: string;
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
}

interface GoogleCalendarIntegrationConfig {
  apiKey: string;
  calendarId: string; // Business calendar ID
  timeZone: string;
  syncEnabled: boolean;
}

export class GoogleCalendarIntegrationService {
  private apiKey: string | null = null;
  private baseUrl = 'https://www.googleapis.com/calendar/v3';

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  }

  private isApiAvailable(): boolean {
    return !!this.apiKey;
  }

  /**
   * Creates a Google Calendar event from a shift
   */
  private shiftToCalendarEvent(
    shift: DayShift, 
    employeeName: string, 
    businessName: string,
    timeZone: string = 'UTC'
  ): GoogleCalendarEvent {
    const startDateTime = shift.startTime.toISOString();
    const endDateTime = shift.endTime.toISOString();

    return {
      summary: `${employeeName} - ${shift.title || 'Work Shift'}`,
      description: `Shift for ${employeeName} at ${businessName}${shift.title ? `\nRole: ${shift.title}` : ''}`,
      start: {
        dateTime: startDateTime,
        timeZone
      },
      end: {
        dateTime: endDateTime,
        timeZone
      },
      location: businessName,
      attendees: employeeName ? [{
        email: `${employeeName.toLowerCase().replace(/\s+/g, '.')}@company.com`, // This would need to be actual employee email
        displayName: employeeName
      }] : undefined
    };
  }

  /**
   * Creates a shift from a Google Calendar event
   */
  private calendarEventToShift(event: GoogleCalendarEvent, businessId: string): Partial<DayShift> {
    return {
      startTime: parseISO(event.start.dateTime),
      endTime: parseISO(event.end.dateTime),
      title: event.summary,
      // Additional mapping would be needed for employee assignment
    };
  }

  /**
   * Exports a shift to Google Calendar
   */
  async exportShiftToCalendar(
    shift: DayShift,
    calendarId: string,
    employeeName: string,
    businessName: string,
    timeZone: string = 'UTC'
  ): Promise<string | null> {
    if (!this.isApiAvailable()) {
      console.warn('Google Calendar API not available');
      return null;
    }

    try {
      const calendarEvent = this.shiftToCalendarEvent(shift, employeeName, businessName, timeZone);
      
      const response = await fetch(
        `${this.baseUrl}/calendars/${encodeURIComponent(calendarId)}/events?key=${this.apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(calendarEvent)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create calendar event: ${response.statusText}`);
      }

      const createdEvent = await response.json();
      console.log(`Exported shift to Google Calendar: ${createdEvent.id}`);
      return createdEvent.id;
    } catch (error) {
      console.error('Error exporting shift to calendar:', error);
      return null;
    }
  }

  /**
   * Imports calendar events as shifts for a specific date range
   */
  async importCalendarEventsAsShifts(
    calendarId: string,
    startDate: Date,
    endDate: Date,
    businessId: string
  ): Promise<Partial<DayShift>[]> {
    if (!this.isApiAvailable()) {
      console.warn('Google Calendar API not available');
      return [];
    }

    try {
      const url = new URL(`${this.baseUrl}/calendars/${encodeURIComponent(calendarId)}/events`);
      url.searchParams.set('key', this.apiKey!);
      url.searchParams.set('timeMin', startDate.toISOString());
      url.searchParams.set('timeMax', endDate.toISOString());
      url.searchParams.set('singleEvents', 'true');
      url.searchParams.set('orderBy', 'startTime');

      const response = await fetch(url.toString());
      
      if (!response.ok) {
        throw new Error(`Failed to fetch calendar events: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.items) {
        return [];
      }

      const shifts = data.items
        .filter((event: GoogleCalendarEvent) => event.start.dateTime && event.end.dateTime)
        .map((event: GoogleCalendarEvent) => this.calendarEventToShift(event, businessId));

      console.log(`Imported ${shifts.length} calendar events as potential shifts`);
      return shifts;
    } catch (error) {
      console.error('Error importing calendar events:', error);
      return [];
    }
  }

  /**
   * Checks for holiday conflicts with shifts
   */
  async checkHolidayConflicts(
    shifts: DayShift[],
    holidays: Holiday[],
    businessId: string
  ): Promise<Array<{ shift: DayShift; holiday: Holiday; severity: 'warning' | 'error' }>> {
    const conflicts: Array<{ shift: DayShift; holiday: Holiday; severity: 'warning' | 'error' }> = [];

    shifts.forEach(shift => {
      const shiftDate = format(shift.startTime, 'yyyy-MM-dd');
      const conflictingHoliday = holidays.find(holiday => holiday.date === shiftDate);
      
      if (conflictingHoliday) {
        const severity = conflictingHoliday.isNonWorkingDay ? 'error' : 'warning';
        conflicts.push({ shift, holiday: conflictingHoliday, severity });
      }
    });

    // Store conflicts in Supabase for reporting
    if (conflicts.length > 0) {
      try {
        const conflictRecords = conflicts.map(conflict => ({
          business_id: businessId,
          shift_id: conflict.shift.id,
          holiday_date: conflict.holiday.date,
          holiday_name: conflict.holiday.name,
          severity: conflict.severity,
          resolved: false,
          created_at: new Date().toISOString()
        }));

        await supabase
          .from('holiday_shift_conflicts')
          .upsert(conflictRecords, { onConflict: 'business_id,shift_id,holiday_date' });
      } catch (error) {
        console.warn('Failed to store holiday conflicts:', error);
      }
    }

    return conflicts;
  }

  /**
   * Suggests optimal shift times avoiding holidays and conflicts
   */
  suggestAlternativeShiftTimes(
    originalShift: Partial<DayShift>,
    holidays: Holiday[],
    existingShifts: DayShift[]
  ): Array<{ startTime: Date; endTime: Date; reason: string }> {
    const suggestions: Array<{ startTime: Date; endTime: Date; reason: string }> = [];
    
    if (!originalShift.startTime || !originalShift.endTime) {
      return suggestions;
    }

    const shiftDate = format(originalShift.startTime, 'yyyy-MM-dd');
    const conflictingHoliday = holidays.find(h => h.date === shiftDate);

    if (conflictingHoliday?.isNonWorkingDay) {
      // Suggest moving to next business day
      const nextDay = new Date(originalShift.startTime);
      nextDay.setDate(nextDay.getDate() + 1);
      
      // Check if next day is also a holiday
      while (holidays.some(h => h.date === format(nextDay, 'yyyy-MM-dd') && h.isNonWorkingDay)) {
        nextDay.setDate(nextDay.getDate() + 1);
      }

      const duration = originalShift.endTime.getTime() - originalShift.startTime.getTime();
      const newStartTime = new Date(nextDay);
      newStartTime.setHours(originalShift.startTime.getHours(), originalShift.startTime.getMinutes());
      
      const newEndTime = new Date(newStartTime.getTime() + duration);

      suggestions.push({
        startTime: newStartTime,
        endTime: newEndTime,
        reason: `Moved to ${format(nextDay, 'EEEE, MMM d')} to avoid ${conflictingHoliday.name}`
      });
    }

    return suggestions;
  }

  /**
   * Bulk export shifts to Google Calendar
   */
  async bulkExportShifts(
    shifts: DayShift[],
    calendarId: string,
    businessName: string,
    timeZone: string = 'UTC'
  ): Promise<{ exported: number; failed: number; errors: string[] }> {
    const results = { exported: 0, failed: 0, errors: [] as string[] };

    for (const shift of shifts) {
      try {
        const employeeName = shift.employeeName || 'Unassigned';
        const eventId = await this.exportShiftToCalendar(shift, calendarId, employeeName, businessName, timeZone);
        
        if (eventId) {
          results.exported++;
          
          // Store the Google Calendar event ID for future sync
          if (shift.id) {
            await supabase
              .from('shifts')
              .update({ google_calendar_event_id: eventId })
              .eq('id', shift.id);
          }
        } else {
          results.failed++;
        }
      } catch (error) {
        results.failed++;
        const errorMsg = `Failed to export shift ${shift.id}: ${error instanceof Error ? error.message : 'Unknown error'}`;
        results.errors.push(errorMsg);
        console.error(errorMsg);
      }
    }

    return results;
  }

  /**
   * Creates a business calendar if it doesn't exist
   */
  async createBusinessCalendar(businessName: string): Promise<string | null> {
    if (!this.isApiAvailable()) {
      console.warn('Google Calendar API not available');
      return null;
    }

    try {
      const calendarData = {
        summary: `${businessName} - Staff Schedule`,
        description: `Employee shift schedule for ${businessName}`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const response = await fetch(
        `${this.baseUrl}/calendars?key=${this.apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(calendarData)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create calendar: ${response.statusText}`);
      }

      const calendar = await response.json();
      console.log(`Created business calendar: ${calendar.id}`);
      return calendar.id;
    } catch (error) {
      console.error('Error creating business calendar:', error);
      return null;
    }
  }
}

// Create singleton instance
export const googleCalendarIntegration = new GoogleCalendarIntegrationService();

// Hook for using Google Calendar integration in components
export function useGoogleCalendarIntegration() {
  return googleCalendarIntegration;
}